import { useEffect, useMemo, useRef, useState } from 'react';
import styled, { keyframes, css } from 'styled-components';
import useTranslation from '../../../../hooks/useTranslation';

import CardList from './CardList';

const flash = keyframes`
    0% { opacity: 1; }
    50% { opacity: 0.2; }
    100% { opacity: 1; }
`;

const ListContainer = styled.div`
    width: 100%;
    padding: 0rem;
    ${props => props.$flashing && css`
        animation: ${flash} 1.5s ease-in-out infinite;
    `}
`;

// Legacy list header / row styles kept for backwards compatibility
export const ListHeader = styled.div`
    display: flex;
    justify-content: space-between;
    padding: 0.5rem;
    border-bottom: 1px solid ${props => props.theme.table.border};
    font-weight: bold;
`;

export const ListRow = styled.div`
    display: flex;
    min-height: 3rem;
    justify-content: space-between;
    padding: 0.5rem;
    border-bottom: 1px solid ${props => props.theme.table.border};
    cursor: pointer;
    background-color: ${props =>
        props.selected ? props.theme.table.background : 'transparent'};

    &:hover {
        background-color: ${props => props.theme.table.hover};
    }
`;

export const ListCol = styled.div`
    flex: ${props => props.$flex || '1'};
    margin: ${props => props.$margin || '0'};
    display: flex;
    align-items: center;
    font-size: var(--font-XS);
`;

const SelectList = ({
    columns = ['Location', 'Quantity'],
    emptyMessage,
    data = [],
    biData = [],
    selectedItems = [],
    isItemSelectable = () => true,
    handleSelection,
    getTagValue,
    testId,
    layout,
    action,
    onAction,
}) => {
    const { t } = useTranslation();

    // Guard against null/undefined data arrays
    const safeData = Array.isArray(data) ? data : [];
    const safeBiData = Array.isArray(biData) ? biData : [];
 
    const [activeId, setActiveId] = useState(null);
    const [isFlashing, setIsFlashing] = useState(false);
    const previousSignatureRef = useRef(null);

    // Determine selection when data changes:
    // We only auto-reset the selection when the *content* of the data
    // list changes (ids / matchRatio), not on every re-render. This
    // lets the user change selection freely, but still re-initializes
    // to the best suggestion when the parent refreshes the list.
    useEffect(() => {
        if (safeData.length === 0) {
            setActiveId(null);
            previousSignatureRef.current = null;
            return;
        }

        // Build a lightweight signature based on ids and matchRatio.
        const signature = safeData
            .map(item => `${item?.id}:${item?.matchRatio ?? ''}`)
            .join('|');

        // If the signature hasn't changed, the items are effectively
        // the same list; do not override the user's current selection.
        if (signature === previousSignatureRef.current) {
            return;
        }

        previousSignatureRef.current = signature;

        // By convention the list is already sorted by best suggestion,
        // so the first element is the one we want to preselect.
        const first = safeData[0];

        if (!first || first.id === undefined) {
            setActiveId(null);
            return;
        }

        setActiveId(first.id);

        // Notify parent so any external selection state can stay in sync.
        if (handleSelection) {
            handleSelection(first);
        }
    }, [safeData, handleSelection]);

    const selectedItem = useMemo(
        () => safeData.find(item => item?.id === activeId) || null,
        [safeData, activeId]
    );

    const handleItemClick = item => {
        if (!isItemSelectable(item)) return;
 
        setActiveId(item?.id);
 
        if (handleSelection) {
            handleSelection(item);
        }
    };
 
    const handleActionClick = async () => {
        if (!selectedItem || !action || !onAction) return;

        try {
            setIsFlashing(true);
            const maybePromise = onAction(selectedItem, action);
            if (maybePromise && typeof maybePromise.then === 'function') {
                await maybePromise;
            }
        } finally {
            setIsFlashing(false);
        }
    };

    // Card layout branch (new design)
    if (layout) {
        return (
            <ListContainer $flashing={isFlashing}>
                {safeData.length === 0 && safeBiData.length === 0 && (
                    <div>
                        <span>{t(emptyMessage)}</span>
                    </div>
                )}

                <CardList
                    data={safeData}
                    activeId={activeId}
                    layout={layout}
                    action={action}
                    onActionClick={handleActionClick}
                    onItemClick={handleItemClick}
                    testId={testId}
                />
            </ListContainer>
        );
    }
    
    // Return null if no layout is provided (fallback)
    return null;
};

export default SelectList;